import React, { useEffect, useState } from 'react';
import { MinusIcon, PlusIcon } from '@heroicons/react/24/outline';
import type { ConferenciaNfeItem } from '../../types/conferencia';
import { statusItemConferencia, type ToneConferencia } from '../../utils/statusConferencia';
import { inputIris } from '../ui/iris';
import { useConfirmDialog } from '../ui/ConfirmDialog';
import { qtd as fmtQtd } from '../../utils/format';
import { focarScanBar } from './ScanBar';
import FotoProduto from './FotoProduto';

// Mesmo TONE_BADGE das demais telas Íris (ver DivergenciaResumo.tsx / statusConferencia.ts) —
// cada tela mantém a própria cópia (convenção já em uso no módulo, sem export central).
const TONE_BADGE: Record<ToneConferencia, string> = {
  ok: 'bg-ok-soft text-ok',
  warning: 'bg-warning-soft text-warning',
  danger: 'bg-danger-soft text-danger',
  accent: 'bg-accent-soft text-accent-text',
  neutral: 'bg-surface-2 text-tx-3',
};

/** Aceita vírgula ou ponto decimal (leitor/teclado PT-BR); null se inválido ou negativo. */
const parseQtd = (v: string): number | null => {
  const n = Number(v.replace(',', '.').trim());
  return Number.isFinite(n) && n >= 0 ? n : null;
};

/**
 * "60 un" quando a nota veio na unidade base, "60 un (5 CX)" quando veio numa embalagem
 * maior. O conferente compara com o DANFE, que fala em caixas; a contagem interna é sempre
 * em unidades. Mostrar só um dos dois obrigaria ele a fazer a conta de cabeça.
 */
function descreverQtdNota(item: ConferenciaNfeItem): string {
  const base = fmtQtd(item.qtdNota);
  if (item.qtdEmbalagem <= 1) return item.unidade ? `${base} ${item.unidade}` : base;

  const emEmbalagens = item.qtdNota / item.qtdEmbalagem;
  return `${base} un (${fmtQtd(emEmbalagens)} ${item.unidade ?? 'emb'})`;
}

interface ItemConferenciaLinhaProps {
  item: ConferenciaNfeItem;
  /** true logo após este item ser bipado — feedback visual do scan (ver DESTAQUE_MS). */
  destacado?: boolean;
  /** Conferência `concluida` — todo input vira somente-leitura. */
  bloqueado: boolean;
  /** Gate de `parametros.conferenciaContagemManual`, lido via `conferencia.contagemManualPermitida`
      (enriquecido no backend — ver ConferenciaNfeService.ContagemManualPermitidaAsync). Quando
      false, a contagem só muda por bipagem; avaria continua editável (não dá pra "bipar" avaria). */
  permiteContagemManual: boolean;
  /** `permitirExcedente` = o operador confirmou um número acima da nota (vira excedente). */
  onAlterarContagem: (novaQtd: number, permitirExcedente: boolean) => void;
  onAlterarAvaria: (novaQtdAvariada: number, permitirExcedente: boolean) => void;
  /** Foco/desfoco do input de contagem — o workspace mobile usa pra colapsar a câmera e
      liberar a tela pro teclado enquanto se digita. */
  onFocoContagem?: (focado: boolean) => void;
}

/**
 * Uma linha do checklist de conferência — renderiza card (`<md`) e linha densa (`md+`)
 * do mesmo item, mesmo molde de ConferenciaListPage/XmlTerceirosList. Contagem e avaria
 * usam estado local (string) que sincroniza do prop a cada mudança externa (bipagem,
 * patch otimista) e só dispara `onAlterar*` no blur/Enter — evita 1 mutation por tecla.
 */
const ItemConferenciaLinha: React.FC<ItemConferenciaLinhaProps> = ({
  item,
  destacado = false,
  bloqueado,
  permiteContagemManual,
  onAlterarContagem,
  onAlterarAvaria,
  onFocoContagem,
}) => {
  const confirm = useConfirmDialog();
  const [contagemInput, setContagemInput] = useState(() => fmtQtd(item.qtdConferida));
  const [avariaInput, setAvariaInput] = useState(() => fmtQtd(item.qtdAvariada));

  // Ressincroniza quando a quantidade muda por fora (bipagem via ScanBar, patch otimista,
  // revalidação onSettled) — sem isso o campo "trava" no que foi digitado por último.
  useEffect(() => setContagemInput(fmtQtd(item.qtdConferida)), [item.qtdConferida]);
  useEffect(() => setAvariaInput(fmtQtd(item.qtdAvariada)), [item.qtdAvariada]);

  /**
   * Estourar a nota pela DIGITAÇÃO é permitido (o fornecedor pode ter mandado a mais) — mas
   * é declaração deliberada, então confirma antes e o item vira excedente. Diferente do BIPE,
   * onde estourar zera (a leitura é ambígua). Retorno: `false` = dentro da nota (segue direto);
   * `true` = estoura e o operador confirmou (permitirExcedente); `null` = estoura e cancelou.
   */
  const confirmarExcedente = async (novoTotal: number): Promise<boolean | null> => {
    if (novoTotal <= item.qtdNota) return false;
    const ok = await confirm({
      titulo: 'Registrar acima da nota',
      mensagem: `A nota tem ${fmtQtd(item.qtdNota)}${item.unidade ? ` ${item.unidade}` : ''} deste item, e você está registrando ${fmtQtd(novoTotal)}. Isso marca "${descricao}" como excedente. Confirmar?`,
      textoConfirmar: 'Registrar excedente',
    });
    // O dialog roubou o foco (focou o botão Cancelar); ao fechar, o foco cairia no <body> e o
    // próximo bipe seria digitado no vazio — a ScanBar promete nunca perder um scan. Devolve
    // o foco a ela em qualquer desfecho. Só aqui: sem excedente, nenhum dialog abriu.
    focarScanBar();
    return ok ? true : null;
  };

  // Commit da contagem digitada. Só no blur/Enter (não no onChange, senão o campo "pularia"
  // enquanto se digita). Sempre repõe o input a partir do valor efetivamente persistido — o
  // useEffect acima só dispara quando o prop MUDA, e sem esta reposição um número recusado
  // (cancelado) ou igual ao atual deixaria o campo exibindo algo que não foi gravado.
  const commitContagem = async () => {
    const n = parseQtd(contagemInput);
    if (n === null || n === item.qtdConferida) { setContagemInput(fmtQtd(item.qtdConferida)); return; }

    const excedente = await confirmarExcedente(n + item.qtdAvariada);
    if (excedente === null) { setContagemInput(fmtQtd(item.qtdConferida)); return; } // cancelou

    onAlterarContagem(n, excedente);
    setContagemInput(fmtQtd(n));
  };

  const commitAvaria = async () => {
    const n = parseQtd(avariaInput);
    if (n === null || n === item.qtdAvariada) { setAvariaInput(fmtQtd(item.qtdAvariada)); return; }

    const excedente = await confirmarExcedente(item.qtdConferida + n);
    if (excedente === null) { setAvariaInput(fmtQtd(item.qtdAvariada)); return; }

    onAlterarAvaria(n, excedente);
    setAvariaInput(fmtQtd(n));
  };

  const blurOnEnter = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') e.currentTarget.blur();
  };

  const status = statusItemConferencia(item.status);
  const descricao = item.descricao || 'Produto não identificado';
  const semCadastro = !item.produto;

  // Teto atingido. O "+1" (nudge de 1) some no teto: um toque a mais não deve virar decisão
  // de excedente — pra registrar sobra, o operador bipa a caixa (ou DIGITA o número real,
  // que confirma). A bipagem por embalagem casa qualquer EAN da família (ver ScanBar), então
  // não há mais botão manual de somar embalagem — o leitor é o caminho.
  const noTeto = item.qtdConferida + item.qtdAvariada >= item.qtdNota;
  const avisoTeto = `Contagem fechou a quantidade da nota (${fmtQtd(item.qtdNota)}) — para registrar sobra, digite o total recebido`;

  const renderContagem = (dense: boolean) => {
    if (!permiteContagemManual || bloqueado) {
      return (
        <span
          className="text-sm font-semibold tabular-nums text-tx"
          title={bloqueado ? 'Conferência concluída' : 'Contagem manual desativada — use a bipagem'}
        >
          {fmtQtd(item.qtdConferida)}
        </span>
      );
    }
    const btnSz = dense ? 'h-8 w-8' : 'h-11 w-11';
    const inputSz = dense ? 'h-8 w-14' : 'h-11 w-16';
    return (
      <div className="flex items-center gap-1">
        <button
          type="button"
          aria-label={`Diminuir contagem de ${descricao}`}
          onClick={() => onAlterarContagem(Math.max(0, item.qtdConferida - 1), false)}
          className={`flex ${btnSz} flex-shrink-0 items-center justify-center rounded-iris-sm border border-line-strong text-tx-2 hover:bg-surface-2`}
        >
          <MinusIcon className="h-4 w-4" />
        </button>
        <input
          type="text"
          inputMode="decimal"
          value={contagemInput}
          onChange={(e) => setContagemInput(e.target.value)}
          onFocus={() => onFocoContagem?.(true)}
          onBlur={() => { onFocoContagem?.(false); commitContagem(); }}
          onKeyDown={blurOnEnter}
          aria-label={`Contagem de ${descricao}`}
          className={`${inputIris} ${inputSz} text-center tabular-nums`}
        />
        <button
          type="button"
          aria-label={`Aumentar contagem de ${descricao}`}
          disabled={noTeto}
          title={noTeto ? avisoTeto : undefined}
          onClick={() => onAlterarContagem(item.qtdConferida + 1, false)}
          className={`flex ${btnSz} flex-shrink-0 items-center justify-center rounded-iris-sm border border-line-strong text-tx-2 hover:bg-surface-2 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-transparent`}
        >
          <PlusIcon className="h-4 w-4" />
        </button>
      </div>
    );
  };

  const renderAvaria = (dense: boolean) => (
    <input
      type="text"
      inputMode="decimal"
      disabled={bloqueado}
      value={avariaInput}
      onChange={(e) => setAvariaInput(e.target.value)}
      onBlur={commitAvaria}
      onKeyDown={blurOnEnter}
      aria-label={`Avaria de ${descricao}`}
      className={`${inputIris} ${dense ? 'h-8 w-14' : 'h-11 w-16'} text-center tabular-nums ${
        item.qtdAvariada > 0 ? 'border-danger text-danger' : ''
      }`}
    />
  );

  return (
    <div
      // `scroll-mt-40` compensa a ScanBar sticky: sem isso, o scrollIntoView do item bipado
      // pararia com a linha escondida atrás da barra (ver ConferenciaWorkspacePage).
      id={`item-conferencia-${item.id}`}
      className={`scroll-mt-40 transition-colors duration-700 ${
        destacado ? 'bg-accent-soft ring-2 ring-inset ring-accent' : ''
      }`}
    >
      {/* ── Mobile (<md): card ── */}
      <div className="space-y-2.5 p-3 md:hidden">
        <div className="flex items-start gap-2.5">
          <FotoProduto codigo={item.produto} descricao={descricao} tamanho={48} />
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-medium text-tx">{descricao}</p>
            <p className="mt-0.5 text-[11px] text-tx-3">
              {item.produto && <span className="font-mono">{item.produto}</span>}
              {semCadastro && <span className="text-warning"> · sem cadastro</span>}
              {' · '}Nota: {descreverQtdNota(item)}
            </p>
          </div>
          <span className={`flex-shrink-0 rounded-full px-2 py-0.5 text-[11px] font-medium ${TONE_BADGE[status.tone]}`}>
            {status.label}
          </span>
        </div>
        <div className="flex items-center justify-between gap-3">
          <div>
            <div className="mb-1 text-[10px] uppercase tracking-wide text-tx-3">Contagem</div>
            {renderContagem(false)}
          </div>
          <div>
            <div className="mb-1 text-[10px] uppercase tracking-wide text-tx-3">Avaria</div>
            {renderAvaria(false)}
          </div>
        </div>
      </div>

      {/* ── Desktop (md+): linha densa ── */}
      <div className="hidden grid-cols-12 items-center gap-2 px-4 py-2 text-[13px] md:grid">
        <div className="col-span-5 flex min-w-0 items-center gap-2.5">
          <FotoProduto codigo={item.produto} descricao={descricao} tamanho={36} />
          <div className="min-w-0">
            <p className="truncate font-medium text-tx" title={descricao}>{descricao}</p>
            <p className="text-[11px] text-tx-3">
              {item.produto && <span className="font-mono">{item.produto}</span>}
              {semCadastro && <span className="text-warning"> · sem cadastro</span>}
            </p>
          </div>
        </div>
        <div className="col-span-2 min-w-0 tabular-nums text-tx-2">{descreverQtdNota(item)}</div>
        <div className="col-span-2 min-w-0">
          {renderContagem(true)}
        </div>
        <div className="col-span-1 min-w-0">{renderAvaria(true)}</div>
        <div className="col-span-2 min-w-0">
          <span className={`inline-flex rounded-full px-2 py-0.5 text-[11px] font-medium ${TONE_BADGE[status.tone]}`}>
            {status.label}
          </span>
        </div>
      </div>
    </div>
  );
};

export default ItemConferenciaLinha;
